<!-- AlgerianPriceReaderFR.vue -->
<template>
    <div class="algerian-price-reader">
        <button class="btn-speaker" @click.stop="readPrice"
            :title="$t('action.listenPrice') || 'Écouter le prix'"
            :disabled="isReading">
            <span class="mdi"
                  :class="isReading ? 'mdi-loading mdi-spin' : 'mdi-volume-high'"></span>
        </button>
    </div>
</template>

<script>
export default {
    name: "AlgerianPriceReader",
    props: {
        price: {
            type: [String, Number],
            required: true
        }
    },
    data() {
        return {
            frenchVoice: null,
            isReading: false
        };
    },
    mounted() {
        this.loadVoices();
        if (window.speechSynthesis) {
            window.speechSynthesis.onvoiceschanged = this.loadVoices;
        }
    },
    methods: {
        loadVoices() {
            if (!window.speechSynthesis) return;
            const voices = window.speechSynthesis.getVoices();
            this.frenchVoice = voices.find(v => v.lang.startsWith("fr"));
        },

        /* ------------------------------------------------------------------
           Convert number to French (dinars algériens)
        ------------------------------------------------------------------ */
        convertToFrench(amount) {
            amount = amount * 100; // convertir en centimes

            if (amount === 0) return "zéro";

            const units = ["", "un", "deux", "trois", "quatre", "cinq",
                           "six", "sept", "huit", "neuf"];

            const tens = ["", "dix", "vingt", "trente", "quarante", "cinquante",
                          "soixante", "soixante-dix", "quatre-vingt",
                          "quatre-vingt-dix"];

            const special = {
                11: "onze", 12: "douze", 13: "treize", 14: "quatorze",
                15: "quinze", 16: "seize",

                21: "vingt et un", 31: "trente et un",
                41: "quarante et un", 51: "cinquante et un",
                61: "soixante et un", 71: "soixante et onze",
                81: "quatre-vingt-un", 91: "quatre-vingt-onze"
            };

            let result = [];

            // Milliards
            const milliards = Math.floor(amount / 1000000000);
            if (milliards > 0) {
                result.push(this.convertNumberSimple(milliards, units, tens, special) +
                    (milliards > 1 ? " milliards" : " milliard"));
                amount %= 1000000000;
            }

            // Millions
            const millions = Math.floor(amount / 1000000);
            if (millions > 0) {
                result.push(this.convertNumberSimple(millions, units, tens, special) +
                    (millions > 1 ? " millions" : " million"));
                amount %= 1000000;
            }

            // Milliers
            const thousands = Math.floor(amount / 1000);
            if (thousands > 0) {
                if (thousands === 1) {
                    result.push("mille");
                } else {
                    result.push(this.convertNumberSimple(thousands, units, tens, special) + " mille");
                }
                amount %= 1000;
            }

            // Reste
            if (amount > 0) {
                result.push(this.convertNumberSimple(amount, units, tens, special));
            }

            return result.join(" ");
        },

        /* ------------------------------------------------------------------
           Convert simple number (<1000)
        ------------------------------------------------------------------ */
        convertNumberSimple(n, units, tens, special) {
            if (n < 10) return units[n];
            if (special[n]) return special[n];

            if (n < 100) {
                const d = Math.floor(n / 10);
                const u = n % 10;

                if (d === 7 || d === 9) {
                    const base = (d === 7) ? "soixante" : "quatre-vingt";
                    return base + (special[10 + u] ? "-" + special[10 + u] :
                        (u ? "-" + units[u] : ""));
                }

                if (u === 1 && d !== 8) return tens[d] + " et un";
                if (u === 0) return tens[d];

                return tens[d] + "-" + units[u];
            }

            // centaines
            const c = Math.floor(n / 100);
            const reste = n % 100;

            let txt = c === 1 ? "cent" : units[c] + " cents";

            if (reste > 0) txt += " " + this.convertNumberSimple(reste, units, tens, special);

            return txt;
        },

        /* ------------------------------------------------------------------
           Read price (TTS)
        ------------------------------------------------------------------ */
        readPrice() {
            if (!window.speechSynthesis) {
                alert("Votre navigateur ne supporte pas la lecture vocale.");
                return;
            }

            if (this.isReading) return;

            const text = this.convertToFrench(this.price) ;

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = "fr-FR";
            if (this.frenchVoice) utterance.voice = this.frenchVoice;
            utterance.rate = 0.95;

            utterance.onstart = () => this.isReading = true;
            utterance.onend = () => this.isReading = false;
            utterance.onerror = () => this.isReading = false;

            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(utterance);
        }
    }
};
</script>

<style scoped>
.algerian-price-reader {
    display: inline-block;
}

.btn-speaker {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    font-size: 20px;
    color: var(--color-nz-200);
}

.btn-speaker:hover:not(:disabled) {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-speaker:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.mdi-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
